const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const SEASON_KIND_RANK = {
  gun_stalk: 4,
  muzzleloader: 3,
  youth_gun: 2,
  archery: 1,
};

export const SEASON_KIND_LABEL = {
  archery: 'Archery',
  youth_gun: 'Youth gun',
  muzzleloader: 'Muzzleloader',
  gun_stalk: 'Gun',
};

export const SEASON_SEX_LABEL = {
  either: 'either-sex',
  antlered: 'antlered-only',
};

function pad2(value) {
  return String(value).padStart(2, '0');
}

export function monthDayLabel(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return '';
  return `${MONTHS[month - 1]} ${day}`;
}

export function monthBounds(year, monthIndex) {
  const last = new Date(year, monthIndex + 1, 0).getDate();
  return {
    start: `${year}-${pad2(monthIndex + 1)}-01`,
    end: `${year}-${pad2(monthIndex + 1)}-${pad2(last)}`,
  };
}

export function periodOverlapsRange(period, start, end) {
  return Boolean(period?.starts_on && period?.ends_on && period.starts_on <= end && period.ends_on >= start);
}

export function periodCoversDay(period, ymd) {
  return Boolean(period?.starts_on && period?.ends_on && period.starts_on <= ymd && ymd <= period.ends_on);
}

export function dominantSeasonKind(ymd, periods) {
  const current = (periods || []).filter((period) => periodCoversDay(period, ymd));
  if (!current.length) return null;
  return current.reduce((best, period) => (
    (SEASON_KIND_RANK[period.kind] || 0) > (SEASON_KIND_RANK[best.kind] || 0) ? period : best
  )).kind;
}

function formatRange(start, end) {
  return `${monthDayLabel(start)}–${monthDayLabel(end)}`;
}

function kindLabel(period) {
  return SEASON_KIND_LABEL[period.kind] || period.label || period.kind;
}

function sexLabel(period) {
  return SEASON_SEX_LABEL[period.sex] || period.sex || '';
}

export function seasonLegendItems(periods, year, monthIndex) {
  const { start, end } = monthBounds(year, monthIndex);
  return (periods || [])
    .filter((period) => periodOverlapsRange(period, start, end))
    .sort((a, b) => a.starts_on.localeCompare(b.starts_on) || (SEASON_KIND_RANK[b.kind] || 0) - (SEASON_KIND_RANK[a.kind] || 0))
    .map((period) => ({
      kind: period.kind,
      text: `${kindLabel(period)} · ${sexLabel(period)} · ${formatRange(period.starts_on, period.ends_on)}`,
    }));
}

export function seasonTableRows(periods) {
  return [...(periods || [])]
    .sort((a, b) => a.starts_on.localeCompare(b.starts_on) || (SEASON_KIND_RANK[b.kind] || 0) - (SEASON_KIND_RANK[a.kind] || 0))
    .map((period) => ({
      kind: period.kind,
      kindLabel: kindLabel(period),
      sexLabel: sexLabel(period),
      dates: formatRange(period.starts_on, period.ends_on),
    }));
}

export function seasonTableTitle(huntZone) {
  if (!huntZone?.short_label && !huntZone?.code) return '';
  const label = huntZone.short_label || huntZone.code;
  const year = huntZone.license_year || '';
  return [label, year, 'private land'].filter(Boolean).join(' · ');
}

export function stateSeasonsBlock(huntZone, huntSeasons) {
  const periods = Array.isArray(huntSeasons) ? huntSeasons : [];
  if (!huntZone || !periods.length) {
    return { visible: false, title: '', rows: [], officialUrl: '', disclaimer: '' };
  }
  return {
    visible: true,
    title: seasonTableTitle(huntZone),
    rows: seasonTableRows(periods),
    officialUrl: huntZone.official_url || '',
    disclaimer: huntZone.disclaimer || '',
  };
}

export function huntSeasonCalendarModel({ huntZone, huntSeasons, year, monthIndex }) {
  const block = stateSeasonsBlock(huntZone, huntSeasons);
  if (!block.visible) {
    return { ...block, legend: [] };
  }
  return {
    ...block,
    legend: seasonLegendItems(huntSeasons, year, monthIndex),
  };
}
